import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { FC, MouseEventHandler, ReactNode } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';

// Feed filter rose palette — @onesource/theme has no rose token; #c45569 is
// the rose already used in members-fe (module progress bar).
export const FILTER_ROSE = '#c45569';
export const FILTER_ROSE_BG = '#faf0f2';
export const FILTER_ROSE_DARK = '#8f3c4d';

type Props = {
  label: string;
  icon: ReactNode;
  /** Selected value rendered inline as "Label: Value"; button highlights when set */
  value?: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
};

/**
 * Compact dropdown trigger button for feed filters. Neutral by default;
 * highlights rose and shows the selected value inline when a filter is active.
 */
export const FilterTriggerButton: FC<Props> = ({ label, icon, value, onClick }) => {
  const active = !!value;

  return (
    <Button
      variant="text"
      size="small"
      onClick={onClick}
      startIcon={icon}
      endIcon={<MdKeyboardArrowDown size={16} />}
      sx={{
        textTransform: 'none',
        borderRadius: '10px',
        height: 34,
        fontFamily: 'Figtree, sans-serif',
        fontSize: 13,
        fontWeight: 600,
        px: '12px',
        py: 0,
        whiteSpace: 'nowrap',
        border: 'none',
        // Active filter: rose fill (FILTER_ROSE_BG) with #5a5350 text;
        // transparent when inactive. Both states hover to a soft grey (#F4F4F4).
        bgcolor: active ? FILTER_ROSE_BG : 'transparent',
        transition: 'background-color 0.15s ease, color 0.15s ease',
        // The theme colors text buttons via `root.&.MuiButton-textPrimary`
        // (specificity 0,2,0), which out-ranks a plain sx `color` — so match
        // that class to win.
        '&.MuiButton-textPrimary': { color: active ? '#5a5350' : '#666' },
        '&:hover': { border: 'none' },
        // Soft-grey hover (#F4F4F4). The theme paints a rose hover via its own
        // `.MuiButton-textPrimary:hover` at (0,3,0) specificity, so win with `&&`.
        '&&.MuiButton-textPrimary:hover': { bgcolor: '#F4F4F4' },
        // MUI gives start/end icons -4px outer margins, which makes the left
        // and right padding look uneven — neutralize so both sides match px.
        '& .MuiButton-startIcon': { ml: 0, mr: '6px' },
        '& .MuiButton-endIcon': { ml: '6px', mr: 0 },
      }}
    >
      {active ? (
        <>
          {label}:&nbsp;
          <Box component="span" sx={{ fontWeight: 700 }}>
            {value}
          </Box>
        </>
      ) : (
        label
      )}
    </Button>
  );
};
